/**
 * Fader law: a piecewise-linear map between fader travel (0..1) and decibels,
 * giving most of the travel to the range that matters while mixing
 * (-20..0 dB) and leaving 6 dB of headroom above unity.
 *
 * This is the only fader law the user ever sees: every volume control in the
 * interface (mixer strips, master, Advanced Audio) goes through this file, and
 * the document stores plain linear gain. The audio module ships its own
 * position/gain helper with different breakpoints for its internal use; it must
 * never be mixed with this one in the interface, or the same fader position
 * would mean two different levels.
 */

export const FADER_MIN_DB = -60;
export const FADER_MAX_DB = 6;

/** [dB, fader position] breakpoints, ascending in both columns. */
const CURVE: readonly (readonly [number, number])[] = [
  [FADER_MIN_DB, 0.04],
  [-40, 0.16],
  [-20, 0.42],
  [-10, 0.62],
  [0, 0.84],
  [FADER_MAX_DB, 1],
];

export const UNITY_POSITION = 0.84;

export function dbToGain(db: number): number {
  return db === -Infinity ? 0 : Math.pow(10, db / 20);
}

export function gainToDb(gain: number): number {
  return gain > 0 ? 20 * Math.log10(gain) : -Infinity;
}

export function faderToDb(position: number): number {
  if (!(position > 0)) return -Infinity;
  if (position >= 1) return FADER_MAX_DB;
  const [floorDb, floorPos] = CURVE[0]!;
  // Below the lowest breakpoint the fader runs linearly in amplitude down to silence.
  if (position < floorPos) return gainToDb((dbToGain(floorDb) * position) / floorPos);
  for (let i = 1; i < CURVE.length; i++) {
    const [db1, pos1] = CURVE[i]!;
    if (position <= pos1) {
      const [db0, pos0] = CURVE[i - 1]!;
      return db0 + ((position - pos0) / (pos1 - pos0)) * (db1 - db0);
    }
  }
  return FADER_MAX_DB;
}

export function dbToFader(db: number): number {
  if (Number.isNaN(db) || db === -Infinity) return 0;
  if (db >= FADER_MAX_DB) return 1;
  const [floorDb, floorPos] = CURVE[0]!;
  if (db < floorDb) return (dbToGain(db) / dbToGain(floorDb)) * floorPos;
  for (let i = 1; i < CURVE.length; i++) {
    const [db1, pos1] = CURVE[i]!;
    if (db <= db1) {
      const [db0, pos0] = CURVE[i - 1]!;
      return pos0 + ((db - db0) / (db1 - db0)) * (pos1 - pos0);
    }
  }
  return 1;
}

export function faderToGain(position: number): number {
  return dbToGain(faderToDb(position));
}

export function gainToFader(gain: number): number {
  return dbToFader(gainToDb(gain));
}

export function formatDb(db: number): string {
  if (db === -Infinity || Number.isNaN(db) || db < -99) return '-inf dB';
  const rounded = Math.round(db * 10) / 10;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)} dB`;
}

export function formatPercent(gain: number): string {
  return `${Math.round(gain * 100)}%`;
}
